<template>
  <div class="texto-correo-editor">
    <v-textarea ref="texto" v-bind="$attrs" :value="value" :disabled="disabled" :readonly="readonly"
      v-on="eventosTexto" @select.native="actualizarSeleccion" @keyup.native="actualizarSeleccion"
      @mouseup.native="actualizarSeleccion" @focusin.native="actualizarSeleccion" />
    <div class="d-flex flex-wrap align-center" role="group" aria-label="Formato del texto seleccionado">
      <v-btn v-for="formato in formatos" :key="formato.etiqueta" small outlined class="mr-2 mb-2"
        :title="formato.nombre" :aria-label="formato.nombre" :disabled="!puedeFormatear"
        @mousedown.prevent @click="aplicarFormato(formato.etiqueta)">
        <v-icon small>{{ formato.icono }}</v-icon>
      </v-btn>
      <v-btn v-for="color in colores" :key="color.valor" small outlined class="mr-2 mb-2"
        :title="'Texto ' + color.nombre" :aria-label="'Texto ' + color.nombre" :disabled="!puedeFormatear"
        @mousedown.prevent @click="aplicarColor(color.valor)">
        <span class="muestra-color mr-1" :style="{ backgroundColor: color.valor }" aria-hidden="true" />
        {{ color.nombre }}
      </v-btn>
      <v-btn small outlined class="mr-2 mb-2" title="Quitar las etiquetas HTML de todo el correo"
        :disabled="disabled || readonly || !value" @mousedown.prevent @click="limpiarFormato">
        <v-icon small left>mdi-format-clear</v-icon>Limpiar formato
      </v-btn>
      <v-btn small outlined class="mb-2" :disabled="disabled" @click="vistaPrevia = true">
        <v-icon small left>mdi-eye</v-icon>Vista previa
      </v-btn>
    </div>
    <div class="text-caption mb-3">Selecciona el texto y pulsa un botón para darle formato.</div>
    <v-dialog v-model="vistaPrevia" max-width="950" scrollable>
      <v-card>
        <v-card-title>Vista previa del texto del correo<v-spacer />
          <v-btn icon aria-label="Cerrar vista previa" @click="vistaPrevia = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <iframe v-if="vistaPrevia" class="vista-previa-correo" title="Texto del correo en HTML" sandbox=""
            referrerpolicy="no-referrer" :srcdoc="htmlVistaPrevia" />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="vistaPrevia = false">Cerrar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'TextoCorreoEditor',
  inheritAttrs: false,
  props: {
    value: { type: String, default: '' },
    disabled: Boolean,
    readonly: Boolean,
    estiloProveedor: Boolean,
  },
  data: () => ({
    vistaPrevia: false,
    inicioSeleccion: 0,
    finSeleccion: 0,
    formatos: [
      { nombre: 'Negrita', etiqueta: 'strong', icono: 'mdi-format-bold' },
      { nombre: 'Cursiva', etiqueta: 'em', icono: 'mdi-format-italic' },
      { nombre: 'Subrayado', etiqueta: 'u', icono: 'mdi-format-underline' },
    ],
    colores: [
      { nombre: 'negro', valor: '#000000' },
      { nombre: 'rojo', valor: '#ff0000' },
      { nombre: 'naranja', valor: '#ff9c00' },
      { nombre: 'azul', valor: '#0070c0' },
      { nombre: 'verde', valor: '#008000' },
    ],
  }),
  computed: {
    eventosTexto() {
      return { ...this.$listeners, input: this.actualizarTexto };
    },
    puedeFormatear() {
      return !this.disabled && !this.readonly && this.finSeleccion > this.inicioSeleccion;
    },
    htmlVistaPrevia() {
      // Mismos estilos y saltos de línea que las plantillas de correo del servicio.
      const estilo = this.estiloProveedor
        ? 'margin:32px;font-family:Arial,sans-serif;line-height:1.6;color:#1f2937;'
        : "line-height:1.5;color:rgb(0,112,192);margin:40px;padding:0;font-family:'Calibri Light',Calibri,Arial,sans-serif;";
      const texto = (this.value || '').replace(/\r\n|\n\r|\r|\n/g, salto => '<br />' + salto);
      return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: data:; base-uri 'none'; form-action 'none'"></head><body style="${estilo}"><p>${texto}</p></body></html>`;
    },
  },
  watch: {
    value() { this.$nextTick(this.actualizarSeleccion); },
  },
  methods: {
    obtenerTextarea() {
      return this.$refs.texto && this.$refs.texto.$el.querySelector('textarea');
    },
    actualizarSeleccion() {
      const textarea = this.obtenerTextarea();
      this.inicioSeleccion = textarea ? textarea.selectionStart : 0;
      this.finSeleccion = textarea ? textarea.selectionEnd : 0;
    },
    actualizarTexto(texto) {
      this.$emit('input', texto);
      this.$nextTick(this.actualizarSeleccion);
    },
    aplicarFormato(etiqueta) {
      this.envolverSeleccion(`<${etiqueta}>`, `</${etiqueta}>`);
    },
    aplicarColor(color) {
      this.envolverSeleccion(`<span style="color: ${color};">`, '</span>');
    },
    limpiarFormato() {
      if (this.disabled || this.readonly || !this.value) return;
      const documento = new DOMParser().parseFromString(this.value, 'text/html');
      documento.querySelectorAll('script, style').forEach(elemento => elemento.remove());
      documento.querySelectorAll('br').forEach(elemento => elemento.replaceWith('\n'));
      documento.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr').forEach(elemento => {
        if (elemento.nextSibling && !elemento.nextSibling.textContent.startsWith('\n')) {
          elemento.appendChild(documento.createTextNode('\n'));
        }
      });
      // El contenido sigue enviándose como HTML: conservar los caracteres literales.
      const texto = documento.body.textContent.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      this.$emit('input', texto);
      this.$nextTick(() => {
        const textarea = this.obtenerTextarea();
        if (textarea) textarea.focus();
        this.actualizarSeleccion();
      });
    },
    envolverSeleccion(apertura, cierre) {
      const textarea = this.obtenerTextarea();
      if (!textarea || this.disabled || this.readonly) return;
      const inicio = textarea.selectionStart;
      const fin = textarea.selectionEnd;
      if (inicio === fin) return;
      const texto = textarea.value;
      const scroll = textarea.scrollTop;
      this.$emit('input', texto.slice(0, inicio) + apertura + texto.slice(inicio, fin) + cierre + texto.slice(fin));
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(inicio + apertura.length, fin + apertura.length);
        textarea.scrollTop = scroll;
        this.actualizarSeleccion();
      });
    },
  },
};
</script>

<style scoped>
.texto-correo-editor { width: 100%; min-width: 0; }
.muestra-color { display: inline-block; width: 12px; height: 12px; border: 1px solid #888; border-radius: 50%; }
.vista-previa-correo { width: 100%; height: 55vh; min-height: 280px; border: 1px solid #ddd; background: white; }
</style>
